/**
 * CREATIVE DETONATOR - Modal Factory
 * v1.1 - Centralized modal creation with standard behavior
 *
 * Consolidates 8+ modal implementations into a single factory:
 * - Consistent window-titlebar design
 * - Unified close behavior (button, overlay click, Escape key)
 * - Body scroll lock
 * - Open/close lifecycle hooks
 *
 * v1.1: Now imports escapeHtml from core/sanitize.js (FRONTEND-21)
 *
 * Usage:
 *   import { createModal } from './modal-factory.js';
 *   const modal = createModal({ id: 'my-modal', title: 'My Title', content: '<p>...</p>' });
 *   modal.open();
 *   modal.close();
 */

import { escapeHtml } from '../core/sanitize.js';
import { onDocument, cleanupScope } from '../core/events.js';

// ============================================================================
// CONFIGURATION
// ============================================================================

const MODAL_DEFAULTS = {
  closeOnOverlay: true,
  closeOnEscape: true,
  lockBodyScroll: true,
  showCloseButton: true,
  titlePrefix: '◆ ',
  className: '',
};

// Track active escape handlers to prevent duplicates
const escapeHandlers = new Map();

// ============================================================================
// MODAL FACTORY
// ============================================================================

/**
 * Create a modal with standard structure and behavior
 *
 * @param {Object} config - Modal configuration
 * @param {string} config.id - Unique modal ID
 * @param {string} config.title - Modal title (displayed in titlebar)
 * @param {string} config.content - HTML content for modal body
 * @param {string} [config.footerContent] - HTML content for modal footer
 * @param {boolean} [config.subtitle] - Whether to include subtitle element
 * @param {string} [config.className] - Additional class for retro-modal div
 * @param {boolean} [config.closeOnOverlay=true] - Close when clicking overlay
 * @param {boolean} [config.closeOnEscape=true] - Close on Escape key
 * @param {boolean} [config.lockBodyScroll=true] - Prevent body scroll when open
 * @param {boolean} [config.showCloseButton=true] - Show close button in titlebar
 * @param {string} [config.titlePrefix='◆ '] - Prefix for title
 * @param {Function} [config.onOpen] - Called when modal opens
 * @param {Function} [config.onClose] - Called when modal closes
 * @returns {Object|null} Modal controller with open/close methods
 */
export function createModal(config) {
  const options = { ...MODAL_DEFAULTS, ...config };
  const {
    id,
    title,
    content,
    footerContent = '',
    subtitle = false,
    className = '',
    showCloseButton,
    titlePrefix,
    onOpen,
    onClose,
    closeOnOverlay,
    closeOnEscape,
    lockBodyScroll,
  } = options;

  if (!id) {
    console.error('[ModalFactory] Modal ID is required');
    return null;
  }

  // Check if modal already exists
  let modalEl = document.getElementById(id);

  if (!modalEl) {
    // Generate and inject modal HTML
    const html = generateModalHtml({
      id,
      title: titlePrefix + title,
      content,
      footerContent,
      subtitle,
      className,
      showCloseButton,
    });

    document.body.insertAdjacentHTML('beforeend', html);
    modalEl = document.getElementById(id);
    console.log(`[ModalFactory] Created modal: ${id}`);
  }

  if (!modalEl) {
    console.error(`[ModalFactory] Failed to create modal: ${id}`);
    return null;
  }

  // Create controller object
  const controller = {
    element: modalEl,
    id: id,
    isOpen: false,

    /**
     * Open the modal
     * @param {Object} [data] - Optional data passed to onOpen callback
     */
    open(data = {}) {
      if (this.isOpen) return;

      this.isOpen = true;
      modalEl.classList.add('active');

      if (lockBodyScroll) {
        document.body.style.overflow = 'hidden';
      }

      if (onOpen) {
        onOpen.call(this, data);
      }
    },

    /**
     * Close the modal
     */
    close() {
      if (!this.isOpen) return;

      this.isOpen = false;
      modalEl.classList.remove('active');

      if (lockBodyScroll) {
        document.body.style.overflow = '';
      }

      if (onClose) {
        onClose.call(this);
      }
    },

    /**
     * Set the modal title
     * @param {string} newTitle - New title text
     */
    setTitle(newTitle) {
      const titleEl = modalEl.querySelector('.window-title');
      if (titleEl) {
        titleEl.textContent = titlePrefix + newTitle;
      }
    },

    /**
     * Set the subtitle text
     * @param {string} text - Subtitle text
     */
    setSubtitle(text) {
      const subtitleEl = modalEl.querySelector('.modal-idea-subtitle');
      if (subtitleEl) {
        subtitleEl.textContent = text || '';
      }
    },

    /**
     * Get the modal body element
     * @returns {HTMLElement|null}
     */
    getBody() {
      return modalEl.querySelector('.modal-body');
    },

    /**
     * Get the modal footer element
     * @returns {HTMLElement|null}
     */
    getFooter() {
      return modalEl.querySelector('.modal-footer');
    },

    /**
     * Update the body content
     * @param {string} html - New body HTML
     */
    setBodyContent(html) {
      const body = this.getBody();
      if (body) {
        body.innerHTML = html;
      }
    },

    /**
     * Update the footer content
     * @param {string} html - New footer HTML
     */
    setFooterContent(html) {
      const footer = this.getFooter();
      if (footer) {
        footer.innerHTML = html;
      }
    },

    /**
     * Remove modal from DOM and clean up event listeners
     */
    destroy() {
      removeEventListeners();
      modalEl.remove();
      console.log(`[ModalFactory] Destroyed modal: ${id}`);
    }
  };

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  function handleClose() {
    controller.close();
  }

  function handleOverlayClick(e) {
    if (e.target === modalEl && closeOnOverlay) {
      controller.close();
    }
  }

  function handleEscape(e) {
    if (e.key === 'Escape' && controller.isOpen && closeOnEscape) {
      controller.close();
    }
  }

  // ============================================================================
  // ATTACH EVENT LISTENERS
  // ============================================================================

  // Close button
  const closeBtn = modalEl.querySelector('.modal-close-btn');
  if (closeBtn) {
    closeBtn.addEventListener('click', handleClose);
  }

  // Cancel button (if present)
  const cancelBtn = modalEl.querySelector('.btn-cancel');
  if (cancelBtn) {
    cancelBtn.addEventListener('click', handleClose);
  }

  // Overlay click
  modalEl.addEventListener('click', handleOverlayClick);

  // Escape key - track per-modal to avoid duplicates
  if (closeOnEscape && !escapeHandlers.has(id)) {
    onDocument('keydown', handleEscape, `modal-${id}`);
    escapeHandlers.set(id, true); // Just track that we registered
  }

  // ============================================================================
  // CLEANUP FUNCTION
  // ============================================================================

  function removeEventListeners() {
    if (closeBtn) {
      closeBtn.removeEventListener('click', handleClose);
    }
    if (cancelBtn) {
      cancelBtn.removeEventListener('click', handleClose);
    }
    modalEl.removeEventListener('click', handleOverlayClick);

    // Cleanup scoped escape handler
    cleanupScope(`modal-${id}`);
    escapeHandlers.delete(id);
  }

  return controller;
}

// ============================================================================
// HTML GENERATION
// ============================================================================

/**
 * Generate modal HTML string
 * @private
 */
function generateModalHtml({
  id,
  title,
  content,
  footerContent,
  subtitle,
  className,
  showCloseButton,
}) {
  return `
<div class="modal-overlay" id="${id}">
  <div class="retro-modal ${className}">
    <div class="window-titlebar modal-titlebar">
      ${showCloseButton ? '<div class="window-close modal-close-btn" aria-label="Close modal"></div>' : ''}
      <div class="window-title">${escapeHtml(title)}</div>
    </div>
    ${subtitle ? '<div class="modal-idea-subtitle"></div>' : ''}
    <div class="modal-body">
      ${content}
    </div>
    ${footerContent ? `<div class="modal-footer">${footerContent}</div>` : ''}
  </div>
</div>`;
}

// ============================================================================
// CONVENIENCE FUNCTIONS
// ============================================================================

/**
 * Create a confirmation modal
 *
 * @param {Object} config - Configuration
 * @param {string} config.id - Modal ID
 * @param {string} [config.title='CONFIRM'] - Modal title
 * @param {string} config.message - Confirmation message
 * @param {string} [config.confirmText='CONFIRM'] - Confirm button text
 * @param {string} [config.cancelText='CANCEL'] - Cancel button text
 * @param {string} [config.confirmClass='btn-primary'] - Confirm button class
 * @param {Function} [config.onConfirm] - Called when confirmed
 * @param {Function} [config.onCancel] - Called when cancelled
 * @returns {Object} Modal controller
 */
export function createConfirmModal(config) {
  const {
    id = 'confirm-modal',
    title = 'CONFIRM',
    message,
    confirmText = 'CONFIRM',
    cancelText = 'CANCEL',
    confirmClass = 'btn-primary',
    onConfirm,
    onCancel,
  } = config;

  const modal = createModal({
    id,
    title,
    content: `<p class="confirm-message">${escapeHtml(message)}</p>`,
    footerContent: `
      <button class="btn btn-ghost btn-cancel">${cancelText}</button>
      <button class="btn ${confirmClass} btn-confirm">${confirmText}</button>
    `,
    onOpen() {
      const confirmBtn = this.element.querySelector('.btn-confirm');
      const cancelBtn = this.element.querySelector('.btn-cancel');

      // Single-use confirm handler
      const handleConfirm = () => {
        this.close();
        if (onConfirm) onConfirm();
      };

      // Single-use cancel handler
      const handleCancel = () => {
        this.close();
        if (onCancel) onCancel();
      };

      confirmBtn?.addEventListener('click', handleConfirm, { once: true });
      cancelBtn?.addEventListener('click', handleCancel, { once: true });
    }
  });

  return modal;
}

/**
 * Show a simple alert modal (auto-closes on OK)
 *
 * @param {Object} config - Configuration
 * @param {string} [config.title='ALERT'] - Modal title
 * @param {string} config.message - Alert message
 * @param {string} [config.buttonText='OK'] - Button text
 * @returns {Promise} Resolves when modal is closed
 */
export function showAlert(config) {
  const { title = 'ALERT', message, buttonText = 'OK' } = config;

  return new Promise((resolve) => {
    const modal = createModal({
      id: 'alert-modal-' + Date.now(),
      title,
      content: `<p class="alert-message">${escapeHtml(message)}</p>`,
      footerContent: `<button class="btn btn-primary btn-ok">${buttonText}</button>`,
      onOpen() {
        const okBtn = this.element.querySelector('.btn-ok');
        okBtn?.addEventListener('click', () => {
          this.close();
          resolve();
        }, { once: true });
      },
      onClose() {
        // Clean up after close animation
        setTimeout(() => this.destroy(), 300);
        resolve();
      }
    });

    modal.open();
  });
}

/**
 * Show a confirm dialog and return a Promise
 *
 * @param {Object} config - Configuration (same as createConfirmModal)
 * @returns {Promise<boolean>} Resolves true if confirmed, false if cancelled
 */
export function showConfirm(config) {
  return new Promise((resolve) => {
    const modal = createConfirmModal({
      ...config,
      id: config.id || 'confirm-modal-' + Date.now(),
      onConfirm: () => {
        resolve(true);
        setTimeout(() => modal.destroy(), 300);
      },
      onCancel: () => {
        resolve(false);
        setTimeout(() => modal.destroy(), 300);
      }
    });

    modal.open();
  });
}

// ============================================================================
// FORM MODAL HELPER
// ============================================================================

/**
 * Create a form modal with standard submit/cancel behavior
 *
 * @param {Object} config - Configuration
 * @param {string} config.id - Modal ID
 * @param {string} config.title - Modal title
 * @param {string} config.content - Form HTML content
 * @param {string} [config.submitText='SUBMIT'] - Submit button text
 * @param {string} [config.cancelText='CANCEL'] - Cancel button text
 * @param {Function} config.onSubmit - Called with form data object
 * @returns {Object} Modal controller
 */
export function createFormModal(config) {
  const {
    submitText = 'SUBMIT',
    cancelText = 'CANCEL',
    onSubmit,
    ...rest
  } = config;

  const modal = createModal({
    ...rest,
    footerContent: `
      <button class="btn btn-ghost btn-cancel">${cancelText}</button>
      <button class="btn btn-primary btn-submit">${submitText}</button>
    `,
    onOpen() {
      const submitBtn = this.element.querySelector('.btn-submit');
      submitBtn?.addEventListener('click', () => {
        const formData = collectFormData(this.element);
        if (onSubmit) onSubmit(formData);
      });
    }
  });

  return modal;
}

/**
 * Collect form data from modal inputs
 * @private
 */
function collectFormData(modalEl) {
  const data = {};
  modalEl.querySelectorAll('input, textarea, select').forEach(el => {
    if (el.name) {
      if (el.type === 'checkbox') {
        data[el.name] = el.checked;
      } else if (el.type === 'radio') {
        if (el.checked) data[el.name] = el.value;
      } else {
        data[el.name] = el.value;
      }
    }
  });
  return data;
}

// ============================================================================
// DROPDOWN VARIANT
// ============================================================================

/**
 * Create a dropdown-style modal (positioned near trigger element)
 *
 * @param {Object} config - Same as createModal, plus:
 * @param {number} [config.offsetY=8] - Vertical offset from trigger
 * @returns {Object} Modal controller with enhanced open() method
 */
export function createDropdown(config) {
  const { offsetY = 8, ...rest } = config;

  const modal = createModal({
    ...rest,
    className: `dropdown-modal ${config.className || ''}`.trim(),
    closeOnOverlay: true,
  });

  if (!modal) return null;

  // Override open to position near trigger
  const originalOpen = modal.open.bind(modal);
  modal.open = (trigger) => {
    originalOpen();

    if (trigger) {
      const rect = trigger.getBoundingClientRect();
      const retroModal = modal.element.querySelector('.retro-modal');

      if (retroModal) {
        // Position below trigger, aligned left
        const top = rect.bottom + offsetY;
        const left = Math.max(8, rect.left);

        retroModal.style.cssText = `
          position: fixed;
          top: ${top}px;
          left: ${left}px;
          transform: none;
          margin: 0;
        `;
      }
    }
  };

  return modal;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

// escapeHtml is now imported from ../core/sanitize.js
// Removed local duplicate implementation for FRONTEND-21 (XSS Prevention)

/**
 * Get an existing modal controller by ID
 * Note: This only works for modals created by createModal()
 *
 * @param {string} id - Modal ID
 * @returns {HTMLElement|null} Modal element (not controller)
 */
export function getModalElement(id) {
  return document.getElementById(id);
}

/**
 * Check if a modal is currently open
 * @param {string} id - Modal ID
 * @returns {boolean}
 */
export function isModalOpen(id) {
  const el = document.getElementById(id);
  return el?.classList.contains('active') ?? false;
}

/**
 * Close a modal by ID (useful for external close triggers)
 * @param {string} id - Modal ID
 */
export function closeModalById(id) {
  const el = document.getElementById(id);
  if (el?.classList.contains('active')) {
    el.classList.remove('active');
    document.body.style.overflow = '';
  }
}

// ============================================================================
// GLOBAL EXPORTS (for non-module usage)
// ============================================================================

if (typeof window !== 'undefined') {
  window.ModalFactory = {
    createModal,
    createConfirmModal,
    createFormModal,
    createDropdown,
    showAlert,
    showConfirm,
    isModalOpen,
    closeModalById,
    getModalElement,
  };
}
